<script setup lang="ts">
import BillingPriceDisplay from '@/components/BillingPriceDisplay/BillingPriceDisplay.vue';
import PlanFeatureList from '@/components/PlanFeatureList/PlanFeatureList.vue';
import { useI18n } from '@/i18n';
import { logError } from '@/lib/logError';
import { useBillingStore } from '@/stores/billing';
import type { CurrentSubscriptionInfo, Offerings, PlanData, PlanProduct } from '@/types/billing';
import { Button, Divider, Prose } from '@dabble/quillui';
import { computed } from 'vue';
import { useRouter } from 'vue-router';

const billingStore = useBillingStore();
const { t } = useI18n();
const router = useRouter();

const props = defineProps<{
  product: PlanProduct | null;
  current: CurrentSubscriptionInfo | null;
  selection: 'month' | 'year' | 'lifetime';
  productId: string;
  isTrialing?: boolean;
  recommend?: boolean;
  isLegacy?: boolean;
  offerings: Offerings | null;
}>();

const isCurrentProduct = computed(() => props.current?.product?.id === props.productId);

// A lifetime holder with an upgrade on offer: their own card is the current plan, the upgrade's
// card is priced at the upgrade, and nothing else here is theirs to buy.
const upgrade = computed(() =>
  props.selection === 'lifetime' && billingStore.canUpgradeLifetime ? billingStore.lifetimeUpgrade : null,
);
const isUpgrade = computed(() => upgrade.value?.product?.id === props.productId);

// The subscriber's own product as priced in the offering, so a grandfathered plan
// missing the selected interval still shows a price on its own card. Never for
// other cards: a product without that interval would borrow the current plan's
// price and pass as "Current plan".
const oldPlanOfferings = computed(() =>
  isCurrentProduct.value ? props.current?.offerings?.products?.[props.productId] : undefined,
);

const planData = computed<PlanData | null>(() => {
  const product = props.product;
  if (!product) return null;

  if (props.selection === 'lifetime') {
    return isUpgrade.value ? upgrade.value : (product['one-time'] ?? null);
  }

  if (product[props.selection]) {
    return product[props.selection] ?? null;
  }
  const oldOffers = oldPlanOfferings.value;
  if (oldOffers?.[props.selection]) {
    return oldOffers[props.selection] ?? null;
  }
  return oldOffers?.year ?? oldOffers?.month ?? null;
});

const unavailable = computed(
  () => planData.value === null || (!!upgrade.value && !isUpgrade.value && !isCurrentProduct.value),
);

// When the selected interval has no price, keep the card's price layout with a
// dash placeholder (currency taken from any other interval on the product).
const displayPlan = computed(
  () =>
    planData.value ?? {
      currency:
        props.product?.month?.currency ??
        props.product?.year?.currency ??
        props.product?.['one-time']?.currency ??
        'usd',
      interval: props.selection,
      amount: null as number | null,
    },
);

// A card with no price for the selected interval is never the current plan: with
// no subscription either, both ids are undefined and would otherwise match.
const isCurrentPlan = computed(
  () =>
    (!!upgrade.value && isCurrentProduct.value) || (!!planData.value?.id && planData.value.id === props.current?.id),
);

const name = computed(() => {
  if (props.current && isCurrentProduct.value) {
    return props.current.product?.name ?? props.product?.name ?? '';
  }
  return props.product?.name ?? '';
});

const description = computed(() => {
  if (props.current && isCurrentProduct.value) {
    return props.current.product?.description || props.product?.description || '';
  }
  return props.product?.description || '';
});

const features = computed(() => props.product?.marketing_features ?? oldPlanOfferings.value?.marketing_features ?? []);

const buttonText = computed(() => {
  if (isCurrentPlan.value) {
    return props.isTrialing ? t('checkout_plan_current_trial') : t('checkout_plan_current');
  }
  return isUpgrade.value ? t('checkout_upgrade_plan') : t('checkout_select_plan');
});

async function checkout() {
  if (unavailable.value) return;
  if (isUpgrade.value) {
    if (billingStore.selectLifetimeUpgrade()) router.push('/billing/checkout');
    return;
  }
  // In offer-link mode the overview refresh would revert the grid to the current
  // catalog; the linked offering was already loaded (with subscription state) on
  // mount, so skip it. selectedPlan/product below come from props regardless.
  if (!billingStore.offerToken) await billingStore.fetchBillingOverview();
  billingStore.selectedPlan = planData.value;
  billingStore.selectedPlanId = planData.value?.id ?? null;
  billingStore.product = props.offerings?.products?.[props.productId] as PlanProduct;
  billingStore.selection = props.selection;
  if (billingStore.selectedPlan && planData.value?.id) {
    router.push('/billing/checkout');
  } else {
    logError('Cannot checkout, plan ID is missing.');
  }
}
</script>

<template>
  <article class="plan-card" :class="{ 'plan-card--unavailable': unavailable, 'plan-card--recommended': recommend }">
    <span v-if="recommend" class="plan-card__badge">{{ t('checkout_recommended') }}</span>

    <div class="plan-card__head">
      <h3 class="plan-card__name">{{ name }}</h3>
      <Prose variant="secondary" size="small" align="center" class="plan-card__desc">{{ description }}</Prose>
    </div>

    <div class="plan-card__price">
      <BillingPriceDisplay :plan="displayPlan" />
      <span v-if="isUpgrade" class="plan-card__upgrade-note">{{ t('checkout_upgrade_price') }}</span>
    </div>

    <Button
      class="plan-card__cta"
      variant="primary"
      finish="filled"
      fill-width
      :disabled="unavailable || (isCurrentPlan && !isTrialing)"
      @click="checkout"
    >
      {{ buttonText }}
    </Button>

    <Divider variant="secondary" size="none" class="plan-card__divider" />

    <PlanFeatureList :features="features" class="plan-card__features" />
  </article>
</template>

<style lang="scss" scoped>
.plan-card {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: space('large') space('medium') space('medium');
  background-color: theme('color-fg-app');
  border: 1px solid theme('color-border-primary');
  border-radius: borderRadius('medium');
  box-shadow: elevation('small');
  transition:
    box-shadow 160ms ease,
    border-color 160ms ease;

  &:hover {
    box-shadow: elevation('medium');
  }

  &--unavailable {
    opacity: 0.5;
    filter: grayscale(1);
  }

  &--recommended {
    border-color: theme('color-fg-primary-interactive');
    box-shadow:
      0 0 0 1px theme('color-fg-primary-interactive'),
      elevation('medium');
  }
}

.plan-card__badge {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: space('tiny') space('xsmall');
  border-radius: 9999px;
  background-color: theme('color-fg-primary-interactive');
  color: theme('color-fg-primary-interactive-inverted');
  font-size: fontSize('xsmall');
  font-weight: fontWeight('semibold');
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
}

.plan-card__head {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: space('xxsmall');
  text-align: center;
}

.plan-card__name {
  margin: 0;
  font-family: var(--font-family-ui-serif, ui-serif, Georgia, serif);
  font-size: fontSize('large');
  font-weight: fontWeight('bold');
  color: theme('color-text-primary');
}

.plan-card__desc {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 2.6em;
  line-height: 1.3;
}

.plan-card__price {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 5.5rem;
  margin: space('medium') 0;
}

.plan-card__upgrade-note {
  margin-top: space('xxsmall');
  font-size: fontSize('xsmall');
  font-weight: fontWeight('semibold');
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: theme('color-text-secondary');
}

.plan-card__cta {
  margin-bottom: space('medium');
}

.plan-card__features {
  margin-top: space('medium');
}
</style>
